export type VideoSlotActionId = 'replace-upload' | 'replace-library' | 'trim';

export interface VideoSlotAction {
  id: VideoSlotActionId;
  label: string;
  disabled: boolean;
  disabledReason: string | null;
}

/**
 * What a post's VIDEO slot offers behind its pencil.
 *
 * Mirrors the image popover's grammar deliberately: swap-the-asset first
 * (the thing users actually reach for), heavier editing below it, everything
 * frozen while a compare/candidate review is staged — the same `editFrozen`
 * Task 3 threads through the image popover, for the same reason (a second
 * mutation mid-review clobbers the staged state).
 *
 * `trimAvailable` is a real input, not a feature flag for its own sake: the
 * server trim path depends on an ffmpeg binary that is documented to go
 * missing in the deployed runtime. A Trim button that silently does nothing
 * is the failure mode this repo has shipped most often — so the availability
 * of the path decides whether the control exists at all. Trim is CUT for
 * this launch (Task 12b) — every current caller passes `trimAvailable:
 * false`, so `'trim'` never actually appears; the parameter stays real
 * (rather than deleted) so a future revisit doesn't have to re-derive this
 * shape once a trim path actually exists.
 */
export function resolveVideoSlotActions(args: {
  editFrozen: boolean;
  trimAvailable: boolean;
}): VideoSlotAction[] {
  const { editFrozen, trimAvailable } = args;
  const reason = editFrozen ? 'Finish reviewing the pending edit first' : null;
  const actions: VideoSlotAction[] = [
    {
      id: 'replace-upload',
      label: 'Replace with a different video',
      disabled: editFrozen,
      disabledReason: reason,
    },
    {
      id: 'replace-library',
      label: 'Choose from library',
      disabled: editFrozen,
      disabledReason: reason,
    },
  ];
  if (trimAvailable) {
    actions.push({ id: 'trim', label: 'Trim…', disabled: editFrozen, disabledReason: reason });
  }
  return actions;
}
